import NetInfo, {
  NetInfoState,
} from '@react-native-community/netinfo';

import AppStateService, {
  AppStateHandlerType,
} from './AppStateService';

export type NetInfoChangeHandlerType = (status: boolean) => void;

export class NetInfoService {
  private _listeners: NetInfoChangeHandlerType[];
  private _pendingCallbacks: NetInfoChangeHandlerType[];
  private _pendingPromise: Promise<void | NetInfoState> | null;

  public isConnected: boolean;
  public latestState: NetInfoState | null;

  constructor() {
    this._listeners = [];
    this._pendingCallbacks = [];
    this._pendingPromise = null;
    this.isConnected = true;
    this.latestState = null;

    this._handler = this._handler.bind(this);
    this.addChangeListener = this.addChangeListener.bind(this);
    this.removeChangeListener = this.removeChangeListener.bind(this);
  }

  private _handleIsConnectedState(state: NetInfoState): boolean {
    // For web browsers that report network type "unknown" inConnected can return "null" status,
    // it can mean both either true or false we consider accepting this value as true
    return state.isConnected === null ? true : state.isConnected;
  }

  private _connectivityFetch(): void {
    this._pendingPromise = NetInfo.fetch().then((result) => {
      this._pendingPromise = null;
      this.isConnected = this._handleIsConnectedState(result);

      // run and flush pending callbacks
      this._pendingCallbacks.forEach((cb) => cb(this.isConnected));
      this._pendingCallbacks = [];
    });
  }

  private async _handler(state: NetInfoState): Promise<void> {
    this.latestState = state;

    if (this.isConnected === state.isConnected) {
      return;
    }
    this.isConnected = this._handleIsConnectedState(state);

    this._listeners.forEach((cb) => {
      cb(this.isConnected);
    });
  }

  fetchConnectivity() {
    const promise = new Promise((res) => {
      this._pendingCallbacks.push(res);
    });

    if (!this._pendingPromise) {
      this._connectivityFetch();
    }

    return promise;
  }

  async init() {
    await this.fetchConnectivity();

    NetInfo.addEventListener((state) => this._handler(state));
    AppStateService.addChangeListener(() => {
      // on changing app state get new connectivity status
      this.fetchConnectivity();
    });
  }

  addChangeListener(handler: NetInfoChangeHandlerType): void {
    this._listeners.push(handler);
  }

  removeChangeListener(handler: NetInfoChangeHandlerType): void {
    this._listeners = this._listeners.filter((i) => i !== handler);
  }

  addChangeWithAppStateListener(
    handler: NetInfoChangeHandlerType,
  ): () => void {
    this.addChangeListener(handler);

    handler(this.isConnected);

    const handleAppStateChange: AppStateHandlerType = async (
      appState,
    ) => {
      if (appState === AppStateService.appStates.ACTIVE) {
        handler(this.isConnected);
      }
    };

    AppStateService.addChangeListener(handleAppStateChange);

    return () => {
      this.removeChangeWithAppStateListener(
        handler,
        handleAppStateChange,
      );
    };
  }

  removeChangeWithAppStateListener(
    handler: NetInfoChangeHandlerType,
    handleAppStateChange: AppStateHandlerType,
  ): void {
    this.removeChangeListener(handler);
    AppStateService.removeChangeListener(handleAppStateChange);
  }
}

export default new NetInfoService();
